import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BadgeCheck,
  Check,
  Eye,
  Filter,
  PackageCheck,
  Search,
  Truck,
  X,
} from 'lucide-react';
import { vendorApi } from '../../api/vendorApi';
import {
  formatNumber,
  formatToman,
  persianDigits,
  toNumber,
} from '@/features/reports/lib/format';
import { formatJalaliDate } from '@/lib/format';
import { useAutoRefresh } from '@/lib/hooks/useAutoRefresh';
import JalaliDatePicker from '@/components/JalaliDatePicker';
import { PAYMENT_MESSAGES } from '@/features/orders/paymentCopy';
import ScopedOrderDetailModal from '@/features/orders/components/ScopedOrderDetailModal';
import {
  OrderMiniProgress,
  OrderStatusBadge,
} from '@/features/orders/components/OrderStatusTimeline';
import {
  getVendorAcceptanceLabel,
  getVendorAxisStatusLabel,
} from '@/features/orders/lib/orderStatus';
import '@/features/orders/orders.css';
import './vendorOrders.css';

const PAGE_SIZE = 20;

const ITEM_STATUSES = [
  ['pending', 'در انتظار بررسی'],
  ['accepted', 'تأیید شده'],
  ['processing', 'در حال آماده‌سازی'],
  ['shipped', 'ارسال شده'],
  ['delivered', 'تحویل داده شده'],
  ['rejected', 'رد شده'],
  ['failed', 'ناموفق'],
  ['cancelled', 'لغو شده'],
];

const PAYMENT_STATUSES = [
  ['unpaid', 'پرداخت نشده'],
  ['prepaid_awaiting_cheque_confirmation', 'در انتظار تأیید چک'],
  ['paid', 'پرداخت شده'],
  ['cancelled_with_refund_due', 'بازپرداخت در انتظار'],
];

export default function VendorOrders() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 0 });
  const [state, setState] = useState('loading');
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [trackingNumbers, setTrackingNumbers] = useState({});
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const orderTriggerRef = useRef(null);
  const requestIdRef = useRef(0);
  const queryKeyRef = useRef('');
  const queryKey = JSON.stringify([
    page,
    search,
    statusFilter,
    paymentFilter,
    dateFrom,
    dateTo,
  ]);
  // Updated in an effect (never during render) so in-flight responses can be
  // compared against the current filter set once they settle.
  useEffect(() => {
    queryKeyRef.current = queryKey;
  }, [queryKey]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(async ({ background = false } = {}) => {
    const requestId = ++requestIdRef.current;
    const requestKey = queryKey;
    if (!background) {
      setState('loading');
      setError('');
    }
    try {
      const nextData = await vendorApi.getOrders({
        page,
        size: PAGE_SIZE,
        search: search || undefined,
        status: statusFilter || undefined,
        payment_status: paymentFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      if (requestId !== requestIdRef.current || requestKey !== queryKeyRef.current) return;
      setData(nextData);
      setState('ready');
    } catch (requestError) {
      if (requestId !== requestIdRef.current || requestKey !== queryKeyRef.current) return;
      if (background) {
        console.warn('Vendor orders background refresh failed', requestError);
        return;
      }
      console.error(requestError);
      setError('خطا در دریافت سفارش‌ها. لطفاً دوباره تلاش کنید.');
      setState('error');
    }
  }, [dateFrom, dateTo, page, paymentFilter, queryKey, search, statusFilter]);

  const refreshNow = useAutoRefresh(load, { refreshKey: queryKey });

  const runOrderAction = async (orderId, action, failureMessage = PAYMENT_MESSAGES.orderActionFailed) => {
    setUpdatingId(orderId);
    setError('');
    try {
      await action();
      await refreshNow();
    } catch (requestError) {
      console.error(requestError);
      setError(failureMessage);
    } finally {
      setUpdatingId(null);
    }
  };

  const decideCheques = (order, decision) => {
    const prompt = decision === 'confirm'
      ? PAYMENT_MESSAGES.chequeDecisionConfirmBody
      : PAYMENT_MESSAGES.chequeDecisionRejectBody;
    if (!window.confirm(`${PAYMENT_MESSAGES.chequeDecisionTitle}\n${prompt}`)) return;
    runOrderAction(order.id, () => vendorApi.decideCheques(order.id, decision), PAYMENT_MESSAGES.chequeDecisionError);
  };

  const handover = (orderId) => {
    const trackingNumber = trackingNumbers[orderId]?.trim();
    if (!trackingNumber) {
      setError('کد رهگیری را وارد کنید.');
      return;
    }
    return runOrderAction(orderId, () =>
      vendorApi.handover(orderId, { tracking_number: trackingNumber }),
    );
  };

  const pack = (orderId) =>
    runOrderAction(orderId, () => vendorApi.pack(orderId, {}));

  const closeOrderDetail = useCallback(() => setSelectedOrderId(null), []);

  return (
    <section className="vendor-orders" dir="rtl">
      <header className="admin-page-header vendor-orders-header">
        <div>
          <h1 className="admin-page-title">سفارش‌ها</h1>
          <p className="admin-page-subtitle">سفارش‌های مربوط به محصولات فروشگاه شما</p>
        </div>
      </header>

      {error && state !== 'error' && <div className="admin-table-error" role="alert">{error}</div>}

      <div className="card vendor-orders-toolbar">
        <label className="vendor-orders-search">
          <span className="sr-only">جست‌وجو با شماره سفارش</span>
          <Search aria-hidden="true" size={16} />
          <input
            autoComplete="off"
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="جست‌وجو با شماره سفارش"
            type="search"
            value={searchInput}
          />
        </label>
        <label className="vendor-orders-filter">
          <Filter aria-hidden="true" size={16} />
          <span className="sr-only">فیلتر وضعیت کالای فروشنده</span>
          <select
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(1);
            }}
            value={statusFilter}
          >
            <option value="">همه وضعیت‌ها</option>
            {ITEM_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label className="vendor-orders-filter">
          <span className="sr-only">فیلتر وضعیت پرداخت</span>
          <select
            onChange={(event) => {
              setPaymentFilter(event.target.value);
              setPage(1);
            }}
            value={paymentFilter}
          >
            <option value="">همه وضعیت‌های پرداخت</option>
            {PAYMENT_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label className="vendor-orders-date">
          <span>از تاریخ</span>
          <JalaliDatePicker
            aria-label="از تاریخ"
            max={dateTo || undefined}
            onChange={(value) => {
              setDateFrom(value);
              setPage(1);
            }}
            value={dateFrom}
          />
        </label>
        <label className="vendor-orders-date">
          <span>تا تاریخ</span>
          <JalaliDatePicker
            aria-label="تا تاریخ"
            min={dateFrom || undefined}
            onChange={(value) => {
              setDateTo(value);
              setPage(1);
            }}
            value={dateTo}
          />
        </label>
      </div>

      <div className="card vendor-orders-table-card">
        <div className="admin-table-wrap">
          <table className="admin-table vendor-orders-table">
            <thead>
              <tr>
                <th scope="col">شماره سفارش</th>
                <th scope="col">تاریخ</th>
                <th scope="col">محصولات</th>
                <th scope="col">سهم فروشنده</th>
                <th scope="col">وضعیت سفارش</th>
                <th scope="col">رهگیری</th>
                <th scope="col">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {state === 'loading' ? (
                <tr><td className="admin-table-empty" colSpan={7} role="status">در حال دریافت سفارش‌ها...</td></tr>
              ) : state === 'error' ? (
                <tr>
                  <td className="admin-table-empty" colSpan={7}>
                    <span role="alert">{error}</span>
                    <button className="btn-sm btn-sm-success" onClick={() => void load()} type="button">تلاش دوباره</button>
                  </td>
                </tr>
              ) : data.items.length === 0 ? (
                <tr><td className="admin-table-empty" colSpan={7}>سفارشی با این مشخصات پیدا نشد.</td></tr>
              ) : data.items.map((order) => {
                const items = order.items ?? [];
                const isCancelled = order.delivery_status === 'cancelled';
                const awaitingChequeDecision = !isCancelled && order.cheque_status === 'awaiting_confirmation';
                const hasPending = !isCancelled && !awaitingChequeDecision
                  && items.some(
                    (item) => item.vendor_acceptance_status === 'pending',
                  );
                const canPack = !isCancelled && !awaitingChequeDecision
                  && items.some(
                    (item) => item.vendor_acceptance_status === 'accepted'
                      && ['unfulfilled', 'picking', 'packing'].includes(item.vendor_fulfillment_status),
                  );
                const canHandover = !isCancelled && !awaitingChequeDecision
                  && items.some(
                    (item) => item.vendor_acceptance_status === 'accepted'
                      && ['packed', 'ready_for_pickup'].includes(item.vendor_fulfillment_status),
                  );
                const canDeliver = !isCancelled && !awaitingChequeDecision
                  && items.some(
                    (item) => item.vendor_acceptance_status === 'accepted'
                      && item.vendor_fulfillment_status === 'handed_to_carrier',
                  );
                const sellerShare = items.reduce((sum, item) => sum + toNumber(item.vendor_share), 0);
                const tracking = [...new Set(items.map((item) => item.tracking_number).filter(Boolean))];

                return (
                  <tr key={order.id}>
                    <td>
                      <button
                        aria-label={`نمایش جزئیات سفارش ${persianDigits(order.order_number)}`}
                        className="vendor-order-number"
                        onClick={(event) => {
                          orderTriggerRef.current = event.currentTarget;
                          setSelectedOrderId(order.id);
                        }}
                        type="button"
                      >
                        {persianDigits(order.order_number)}
                      </button>
                    </td>
                    <td className="admin-cell-secondary">
                      {order.created_at ? formatJalaliDate(order.created_at) : '—'}
                    </td>
                    <td className="vendor-order-products">
                      {items.map((item) => (
                        <div key={item.id}>
                          {item.product?.name_fa || 'نام ثبت‌نشده'} × {formatNumber(item.quantity)}
                        </div>
                      ))}
                      {awaitingChequeDecision && (
                        <div className="vendor-cheque-details">
                          <strong>{PAYMENT_MESSAGES.chequeAwaitingConfirmation}</strong>
                          <p>{PAYMENT_MESSAGES.chequePrepaymentLabel}: {formatToman(order.cheque_prepayment_amount)}</p>
                        </div>
                      )}
                    </td>
                    <td>{formatToman(sellerShare)}</td>
                    <td>
                      <div className="ost-cell">
                        <OrderStatusBadge order={order} role="seller" />
                        <OrderMiniProgress order={order} role="seller" />
                        {(() => {
                          const states = new Set();
                          for (const item of items) {
                            const acceptance = item.vendor_acceptance_status;
                            const fulfillment = item.vendor_fulfillment_status;
                            if (acceptance && acceptance !== 'accepted') {
                              states.add(getVendorAcceptanceLabel(acceptance));
                            } else if (acceptance || fulfillment) {
                              states.add(getVendorAxisStatusLabel(acceptance, fulfillment));
                            }
                          }
                          if (states.size === 0) return null;
                          return <span className="ost-substatus">{[...states].join(' · ')}</span>;
                        })()}
                      </div>
                    </td>
                    <td className="admin-cell-secondary">
                      {tracking.length ? tracking.map((number) => <div key={number}>{persianDigits(number)}</div>) : '—'}
                    </td>
                    <td>
                      <div className="vendor-order-actions">
                        <button
                          aria-label={`جزئیات سفارش ${persianDigits(order.order_number)}`}
                          className="vendor-order-icon-button"
                          onClick={(event) => {
                            orderTriggerRef.current = event.currentTarget;
                            setSelectedOrderId(order.id);
                          }}
                          title={`جزئیات سفارش ${persianDigits(order.order_number)}`}
                          type="button"
                        >
                          <Eye aria-hidden="true" size={20} />
                        </button>
                        {awaitingChequeDecision && (
                          <>
                            <button
                              className="vendor-order-pill is-primary"
                              disabled={updatingId === order.id}
                              onClick={() => decideCheques(order, 'confirm')}
                              title={PAYMENT_MESSAGES.chequeConfirmDecision}
                              type="button"
                            >
                              <Check aria-hidden="true" size={18} />
                              <span>{PAYMENT_MESSAGES.chequeConfirmDecision}</span>
                            </button>
                            <button
                              className="vendor-order-pill is-danger"
                              disabled={updatingId === order.id}
                              onClick={() => decideCheques(order, 'reject')}
                              title={PAYMENT_MESSAGES.chequeRejectDecision}
                              type="button"
                            >
                              <X aria-hidden="true" size={18} />
                              <span>{PAYMENT_MESSAGES.chequeRejectDecision}</span>
                            </button>
                          </>
                        )}
                        {hasPending && (
                          <>
                            <button
                              aria-label="تأیید سفارش"
                              className="vendor-order-pill is-primary"
                              disabled={updatingId === order.id}
                              onClick={() => void runOrderAction(order.id, () => vendorApi.respondToOrder(order.id, 'accepted'))}
                              title="تأیید سفارش"
                              type="button"
                            >
                              <Check aria-hidden="true" size={18} />
                              <span>تأیید</span>
                            </button>
                            <button
                              aria-label="رد سفارش"
                              className="vendor-order-pill is-danger"
                              disabled={updatingId === order.id}
                              onClick={() => void runOrderAction(order.id, () => vendorApi.respondToOrder(order.id, 'rejected'))}
                              title="رد سفارش"
                              type="button"
                            >
                              <X aria-hidden="true" size={18} />
                              <span>رد</span>
                            </button>
                          </>
                        )}
                        {canPack && (
                          <button
                            aria-label="ثبت بسته‌بندی"
                            className="vendor-order-pill is-primary"
                            disabled={updatingId === order.id}
                            onClick={() => void pack(order.id)}
                            title="بسته‌بندی سفارش"
                            type="button"
                          >
                            <PackageCheck aria-hidden="true" size={18} />
                            <span>بسته‌بندی</span>
                          </button>
                        )}
                        {canHandover && (
                          <>
                            <label className="sr-only" htmlFor={`tracking-${order.id}`}>کد رهگیری سفارش {persianDigits(order.order_number)}</label>
                            <input
                              autoComplete="off"
                              id={`tracking-${order.id}`}
                              maxLength={64}
                              onChange={(event) => setTrackingNumbers((current) => ({
                                ...current,
                                [order.id]: event.target.value,
                              }))}
                              placeholder="کد رهگیری"
                              type="text"
                              value={trackingNumbers[order.id] ?? ''}
                            />
                            <button
                              aria-label="تحویل به حمل‌کننده"
                              className="vendor-order-pill is-primary"
                              disabled={updatingId === order.id}
                              onClick={() => void handover(order.id)}
                              title="تحویل به حمل‌کننده"
                              type="button"
                            >
                              <Truck aria-hidden="true" size={18} />
                              <span>تحویل به حمل‌کننده</span>
                            </button>
                          </>
                        )}
                        {canDeliver && (
                          <button
                            aria-label="ثبت تحویل"
                            className="vendor-order-pill is-primary"
                            disabled={updatingId === order.id}
                            onClick={() => void runOrderAction(order.id, () => vendorApi.deliverOrder(order.id))}
                            title="ثبت تحویل"
                            type="button"
                          >
                            <BadgeCheck aria-hidden="true" size={18} />
                            <span>ثبت تحویل</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {state === 'ready' && (
          <footer className="admin-pagination vendor-orders-pagination">
            <span>مجموع سفارش‌ها: {formatNumber(data.total ?? 0)}</span>
            <div className="admin-pagination-btns">
              <span>صفحه {formatNumber(data.pages ? page : 0)} از {formatNumber(data.pages ?? 0)}</span>
              <button
                disabled={page <= 1 || state === 'loading'}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                type="button"
              >قبلی</button>
              <button
                disabled={!data.pages || page >= data.pages || state === 'loading'}
                onClick={() => setPage((current) => current + 1)}
                type="button"
              >بعدی</button>
            </div>
          </footer>
        )}
      </div>

      {selectedOrderId && (
        <ScopedOrderDetailModal
          amountLabel="سهم فروشنده"
          getAmount={(items) => items.reduce((sum, item) => sum + toNumber(item.vendor_share), 0)}
          getOrder={vendorApi.getOrder}
          onClose={closeOrderDetail}
          orderId={selectedOrderId}
          triggerRef={orderTriggerRef}
        />
      )}
    </section>
  );
}